// frontend/src/components/BulkAgentUpload.jsx
import React, { useState } from 'react';
import { apiClient, handleResponse } from '../api';
import Papa from 'papaparse';

const BulkAgentUpload = ({ onComplete }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [generatePasswords, setGeneratePasswords] = useState(true);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError('');
    setResults(null);

    if (selectedFile) {
      // Preview the CSV file
      Papa.parse(selectedFile, {
        header: true,
        preview: 5, // Show first 5 rows
        skipEmptyLines: true,
        complete: (result) => {
          setPreview(result);
        },
        error: (err) => {
          setError(`Error parsing CSV: ${err.message}`);
          setPreview(null);
        }
      });
    } else {
      setPreview(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a CSV file first');
      return;
    }

    setUploading(true);
    setError('');
    setResults(null);

    try {
      console.log("Starting agent upload", file.name);
      const formData = new FormData();
      formData.append('agentsCsv', file);
      formData.append('generatePasswords', generatePasswords.toString());

      console.log("Sending request to /admin/agents/bulk-upload");
      const response = await apiClient.post('/admin/agents/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log("Raw API response:", response);
      const result = handleResponse(response);
      console.log("Processed result:", result);
      
      if (result.success) {
        setResults(result.data);
        
        // Add this to force refresh
        if (typeof onComplete === 'function') {
          console.log("Calling onComplete function to refresh agent list");
          onComplete();
        } else {
          console.warn("onComplete not provided or not a function");
          // Fallback - try to refresh using window.location
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      } else {
        setError(result.message || 'Failed to upload agents');
      }
    } catch (err) {
      console.error('Error uploading agents:', err);
      setError(err.response?.data?.message || err.message || 'An error occurred during upload');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create CSV template content
    const header = 'name,email,state_codes,source,notes';
    const exampleRow1 = 'John Doe,john@example.com,"FL,TX,GA",referral,Prefers phone calls';
    const exampleRow2 = 'Jane Smith,jane@example.com,"CA,AZ",website,New agent';
    
    const csvContent = [header, exampleRow1, exampleRow2].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'agent_upload_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Bulk Agent Upload</h2>
      
      <div className="mb-6">
        <p className="text-gray-600 mb-2">
          Upload a CSV file with agent information. The system will automatically create accounts
          and generate passwords if requested.
        </p>
        <button 
          onClick={downloadTemplate} 
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          Download CSV Template
        </button>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select CSV File
        </label>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-medium
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
        />
      </div>

      <div className="mb-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={generatePasswords}
            onChange={(e) => setGeneratePasswords(e.target.checked)}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <span className="ml-2 text-sm text-gray-700">
            Auto-generate memorable passwords for agents
          </span>
        </label>
      </div>

      {preview && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Preview:</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {preview.meta.fields.map((field) => (
                    <th
                      key={field}
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {field}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {preview.data.map((row, i) => (
                  <tr key={i}>
                    {preview.meta.fields.map((field) => (
                      <td key={field} className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                        {row[field] || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Showing first {preview.data.length} of {file ? file.name : ''} ({preview.data.length} rows)
          </p>
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={uploading || !file}
        className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {uploading ? 'Uploading...' : 'Upload Agents'}
      </button>

      {error && (
        <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      {results && (
        <div className="mt-4">
          <h3 className="text-lg font-medium text-gray-800 mb-2">Upload Results</h3>
          
          <div className="bg-green-50 p-3 rounded-md mb-4">
            <p className="text-green-700">
              Successfully created {results.successCount} of {results.totalCount} agents
            </p>
          </div>
          
          {results.agents && results.agents.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Password</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">States</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {results.agents.map((agent) => (
                    <tr key={agent.id}>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{agent.name}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{agent.email}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 font-mono bg-yellow-50">
                        {agent.generatedPassword}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {agent.selectedStates && agent.selectedStates.join(', ')}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">N/A</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {results.errors && results.errors.length > 0 && (
            <div className="mt-4">
              <h4 className="text-md font-medium text-red-800 mb-2">Errors</h4>
              <ul className="bg-red-50 p-3 rounded-md text-sm text-red-700 list-disc list-inside">
                {results.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkAgentUpload;